import React, { useMemo, useState } from "react";
import { useAuth } from "../../AuthContext";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import "../styles/Ranking.css";

const CYCLISTS_LEAGUE_ID = -1;

const Ranking = ({
  generalRankings = [],
  espoirRankings = [],
  giroRankings = [],
  tdfRankings = [],
  vueltaRankings = [],
  cyclists = [],
  userLeagues = [],
  leagueMembersById = {},
}) => {
  const { t } = useTranslation();
  const { userId } = useAuth();
  const navigate = useNavigate();

  // null = ligue générale, -1 = coureurs, sinon id ligue
  const [activeLeagueId, setActiveLeagueId] = useState(null);
  const [ranking, setRanking] = useState("general");
  const [rankingType, setRankingType] = useState("general"); // general | espoir | giro | tdf | vuelta

  const isCyclists = activeLeagueId === CYCLISTS_LEAGUE_ID;
  const isSingleRaceRankingType =
    rankingType === "giro" || rankingType === "tdf" || rankingType === "vuelta";
  const seasonsDisabled = isCyclists || isSingleRaceRankingType;

  const asNum = (v) => {
    if (typeof v === "number") return v;
    if (typeof v === "string") return Number(v) || 0;
    return 0;
  };

  const asInt = (v) => {
    if (typeof v === "number") return Math.trunc(v);
    if (typeof v === "string") {
      const n = parseInt(v, 10);
      return Number.isNaN(n) ? null : n;
    }
    return null;
  };

  const pointsKey = useMemo(() => {
    switch (ranking) {
      case "winter":
        return "winterPoints";
      case "spring":
        return "springPoints";
      case "summer":
        return "summerPoints";
      case "automn":
        return "automnPoints";
      case "tdf":
        return "tdfPoints";
      case "general":
      default:
        return "total_points";
    }
  }, [ranking]);

  const selectedRankings = useMemo(() => {
    switch (rankingType) {
      case "espoir":
        return espoirRankings;
      case "giro":
        return giroRankings;
      case "tdf":
        return tdfRankings;
      case "vuelta":
        return vueltaRankings;
      case "general":
      default:
        return generalRankings;
    }
  }, [rankingType, generalRankings, espoirRankings, giroRankings, tdfRankings, vueltaRankings]);

  const leagues = useMemo(() => {
    const extras = (userLeagues || [])
      .map((l) => ({
        leagueId: asInt(l.leagueId),
        leagueName: (l.leagueName ?? t("ranking.league.fallbackName")).toString(),
      }))
      .filter((l) => l.leagueId != null);

    return [
      { leagueId: null, leagueName: t("ranking.league.general") },
      { leagueId: CYCLISTS_LEAGUE_ID, leagueName: t("ranking.league.cyclists") },
      ...extras,
    ];
  }, [userLeagues, t]);

  const displayedRows = useMemo(() => {
    if (isCyclists) {
      const sorted = [...(cyclists || [])].sort(
        (a, b) => asNum(b.cyclistPoints) - asNum(a.cyclistPoints)
      );

      return sorted.map((r, i) => ({
        _key: `c:${asInt(r.cyclistId ?? r.id) ?? i}`,
        position: i + 1,
        name: (r.name ?? "").toString(),
        points: asNum(r.cyclistPoints),
        row: r,
        isCyclist: true,
      }));
    }

    let tmp = [...(selectedRankings || [])];

    if (activeLeagueId != null) {
      const members = leagueMembersById?.[activeLeagueId] || [];
      if (Array.isArray(members) && members.length > 0) {
        tmp = tmp.filter((r) => {
          const uid = asInt(r.userId);
          return uid != null && members.includes(uid);
        });
      } else {
        tmp = [];
      }
    }

    if (ranking === "general") {
      if (activeLeagueId == null) {
        tmp.sort((a, b) => {
          const pa = asInt(a.position);
          const pb = asInt(b.position);
          if (pa != null && pb != null) return pa - pb;
          return asNum(b.total_points) - asNum(a.total_points);
        });
      } else {
        tmp.sort((a, b) => asNum(b.total_points) - asNum(a.total_points));
      }
    } else {
      tmp.sort((a, b) => asNum(b[pointsKey]) - asNum(a[pointsKey]));
    }

    return tmp.map((r, i) => ({
      _key: `u:${asInt(r.userId) ?? i}`,
      position: i + 1,
      name: (r.name ?? "").toString(),
      points: ranking === "general" ? asNum(r.total_points) : asNum(r[pointsKey]),
      row: r,
      isCyclist: false,
    }));
  }, [
    isCyclists,
    cyclists,
    selectedRankings,
    activeLeagueId,
    leagueMembersById,
    ranking,
    pointsKey,
  ]);

  const handleLeagueClick = (leagueId) => {
    setActiveLeagueId(leagueId);

    if (leagueId === CYCLISTS_LEAGUE_ID) {
      setRanking("general");
    }
  };

  const handleRankingTypeClick = (nextRankingType) => {
    setRankingType(nextRankingType);

    if (
      nextRankingType === "giro" ||
      nextRankingType === "tdf" ||
      nextRankingType === "vuelta"
    ) {
      setRanking("general");
    }
  };

  const handleRowClick = (entry) => {
    if (entry.isCyclist) {
      const id = asInt(entry.row?.cyclistId ?? entry.row?.id);
      if (id != null) navigate(`/cyclistDetails/${id}`);
      return;
    }

    const uid = asInt(entry.row?.userId);
    const teamIdByRankingType = {
      general: 1,
      espoir: 2,
      giro: 3,
      tdf: 4,
      vuelta: 5,
    };
    const teamId = teamIdByRankingType[rankingType] || 1;
    if (uid != null) navigate(`/userTeam/${uid}?teamId=${teamId}`);
  };

  return (
    <section className="my-ranking">
      <div className="my-ranking-choice my-ranking-choice-type">
        <button
          onClick={() => handleRankingTypeClick("general")}
          style={{ backgroundColor: rankingType === "general" ? "#007afd" : "#0056b3" }}
          type="button"
        >
          Général
        </button>

        <button
          onClick={() => handleRankingTypeClick("espoir")}
          style={{ backgroundColor: rankingType === "espoir" ? "#007afd" : "#0056b3" }}
          type="button"
        >
          Espoirs
        </button>

        <button
          onClick={() => handleRankingTypeClick("giro")}
          style={{ backgroundColor: rankingType === "giro" ? "#007afd" : "#0056b3" }}
          type="button"
        >
          {t("ranking.type.giro", { defaultValue: "Giro" })}
        </button>

        <button
          onClick={() => handleRankingTypeClick("tdf")}
          style={{ backgroundColor: rankingType === "tdf" ? "#007afd" : "#0056b3" }}
          type="button"
        >
          {t("ranking.type.tdf", { defaultValue: "Tour de France" })}
        </button>

        <button
          onClick={() => handleRankingTypeClick("vuelta")}
          style={{ backgroundColor: rankingType === "vuelta" ? "#007afd" : "#0056b3" }}
          type="button"
        >
          {t("ranking.type.vuelta", { defaultValue: "Vuelta" })}
        </button>
      </div>

      <div className="my-ranking-choice my-ranking-choice-leagues">
        {leagues.map((l) => {
          const selected = activeLeagueId === l.leagueId;
          return (
            <button
              key={`league_${l.leagueId ?? "general"}`}
              onClick={() => handleLeagueClick(l.leagueId)}
              style={{ backgroundColor: selected ? "#007afd" : "#0056b3" }}
              type="button"
            >
              {l.leagueName}
            </button>
          );
        })}
      </div>

      <div className="my-ranking-choice-ranking">
        <button
          onClick={() => setRanking("general")}
          style={{ backgroundColor: ranking === "general" ? "#007afd" : "#0056b3" }}
          type="button"
        >
          {t("ranking.season.general")}
        </button>

        <button
          onClick={() => setRanking("winter")}
          style={{
            backgroundColor: ranking === "winter" ? "#007afd" : "grey",
            opacity: seasonsDisabled ? 0.5 : 1,
          }}
          disabled={seasonsDisabled}
          type="button"
        >
          {t("ranking.season.winter")}
        </button>

        <button
          onClick={() => setRanking("spring")}
          style={{
            backgroundColor: ranking === "spring" ? "#007afd" : "pink",
            opacity: seasonsDisabled ? 0.5 : 1,
          }}
          disabled={seasonsDisabled}
          type="button"
        >
          {t("ranking.season.spring")}
        </button>

        <button
          onClick={() => setRanking("summer")}
          style={{
            backgroundColor: ranking === "summer" ? "#007afd" : "green",
            opacity: seasonsDisabled ? 0.5 : 1,
          }}
          disabled={seasonsDisabled}
          type="button"
        >
          {t("ranking.season.summer")}
        </button>

        <button
          onClick={() => setRanking("automn")}
          style={{
            backgroundColor: ranking === "automn" ? "#007afd" : "orange",
            opacity: seasonsDisabled ? 0.5 : 1,
          }}
          disabled={seasonsDisabled}
          type="button"
        >
          {t("ranking.season.automn")}
        </button>

        <button
          onClick={() => setRanking("tdf")}
          style={{
            backgroundColor: ranking === "tdf" ? "#007afd" : "purple",
            opacity: seasonsDisabled ? 0.5 : 1,
          }}
          disabled={seasonsDisabled}
          type="button"
        >
          {t("ranking.season.tdf")}
        </button>
      </div>

      <div className="my-ranking-table">
        <div className="my-ranking-table-container">
          <table>
            <thead>
              <tr>
                <th>{t("ranking.table.position")}</th>
                <th>
                  {isCyclists
                    ? t("ranking.table.cyclistName")
                    : t("ranking.table.playerName")}
                </th>
                <th>{t("ranking.table.points")}</th>
              </tr>
            </thead>

            <tbody>
              {displayedRows.length === 0 ? (
                <tr>
                  <td colSpan={3} style={{ padding: 18, textAlign: "center" }}>
                    {isCyclists
                      ? t("ranking.empty.cyclists")
                      : activeLeagueId == null
                        ? t("ranking.empty.players")
                        : t("ranking.empty.leagueMembers")}
                  </td>
                </tr>
              ) : (
                displayedRows.map((entry) => {
                  const isMe =
                    !entry.isCyclist &&
                    String(asInt(entry.row?.userId)) === String(userId);

                  return (
                    <tr
                      key={entry._key}
                      className={!entry.isCyclist && isMe ? "highlighted" : ""}
                      onClick={() => handleRowClick(entry)}
                      style={{ cursor: "pointer" }}
                    >
                      <td>{entry.position}</td>
                      <td>{entry.name}</td>
                      <td>{entry.points}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export default Ranking;
